import { SignalConnection } from "./SignalConnection";
import { Task } from "./Task";

//
// Flambe - Rapid game development
// https://github.com/aduros/flambe/blob/master/LICENSE.txt

export class SignalBase {
  constructor(listener: any) {
    this._head = listener != null ? new SignalConnection(this, listener) : null;
    this._deferredTasks = null;
  }

  /**
   * Whether this signal has at least one listener.
   */
  public hasListeners(): boolean {
    return this._head != null;
  }

  protected connectImpl(listener: any, prioritize: boolean): SignalConnection {
    let conn = new SignalConnection(this, listener);
    if (this.dispatching()) {
      this.defer(() => {
        this.listAdd(conn, prioritize);
      });
    } else {
      this.listAdd(conn, prioritize);
    }
    return conn;
  }

  disconnect(conn: SignalConnection) {
    if (this.dispatching()) {
      this.defer(() => {
        this.listRemove(conn);
      });
    } else {
      this.listRemove(conn);
    }
  }

  protected defer(fn: () => void) {
    let tail: Task | null;
    let p: Task | null = this._deferredTasks;
    while (p != null) {
      tail = p;
      p = p.next as Task;
    }

    let task: Task = { fn };
    if (tail! != null) {
      tail.next = task;
    } else {
      this._deferredTasks = task;
    }
  }

  protected willEmit(): SignalConnection {
    // Should never happen, since the public emit methods will defer, but just in case...
    // Assert.that(!dispatching());

    const snapshot = this._head;
    this._head = DISPATCHING_SENTINEL;
    return snapshot as SignalConnection;
  }

  protected didEmit(head: SignalConnection) {
    this._head = head;

    let snapshot = this._deferredTasks;
    this._deferredTasks = null;
    while (snapshot != null) {
      snapshot.fn();
      snapshot = snapshot.next as Task;
    }
  }

  protected listAdd(conn: SignalConnection, prioritize: boolean) {
    if (prioritize) {
      // Prepend it to the beginning of the list
      conn._next = this._head as SignalConnection;
      this._head = conn;
    } else {
      // Append it to the end of the list
      let tail = null,
        p = this._head;
      while (p != null) {
        tail = p;
        p = p._next as SignalConnection;
      }
      if (tail != null) {
        tail._next = conn;
      } else {
        this._head = conn;
      }
    }
  }

  protected listRemove(conn: SignalConnection) {
    let prev: SignalConnection | null = null,
      p = this._head;
    while (p != null) {
      if (p == conn) {
        // Splice out p
        let next = p._next;
        if (prev == null) {
          this._head = next as SignalConnection;
        } else {
          prev._next = next;
        }
        return;
      }
      prev = p;
      p = p._next as SignalConnection;
    }
  }

  protected dispatching(): boolean {
    return this._head == DISPATCHING_SENTINEL;
  }

  protected _head: SignalConnection | null;
  protected _deferredTasks: Task | null;
}

const DISPATCHING_SENTINEL = new SignalConnection(null, null);
